import { getMergedOptionGroupOptions } from 'shared-helpers/customAttributes';
import { useReduxState } from 'hooks';

import type { AttributeCondition, UserCustomAttribute } from 'shared-types';

interface Props {
  conditionKey: AttributeCondition['key'];
  conditionValue: AttributeCondition['value'];
}

const GroupUserAttributeDisplayValue = ({
  conditionKey,
  conditionValue
}: Props) => {
  const {
    language,
    messages,
    serverConfig: { userCustomAttributes }
  } = useReduxState();

  const accountTypesByKey = {
    admin: messages.usersAdmin,
    superadmin: messages.usersSuperadmin,
    supervisor: messages.adminSupervisor,
    user: messages.commonUser
  };

  const userCustomAttributesByKey: { [key: string]: UserCustomAttribute } = {};
  userCustomAttributes?.forEach((attribute) => {
    userCustomAttributesByKey[attribute.key] = attribute;
  });

  const getValue = () => {
    if (conditionValue === '') {
      return messages.groupsUserAttributesAny;
    }

    if (conditionKey === 'accountType') {
      return accountTypesByKey[conditionValue as string] || conditionValue;
    }

    if (userCustomAttributesByKey[conditionKey]) {
      const attribute = userCustomAttributesByKey[conditionKey];
      if (
        attribute.type === 'ArrayWithOptions' ||
        attribute.type === 'StringWithOptions'
      ) {
        const mergedOptions = getMergedOptionGroupOptions({
          language,
          optionGroups: attribute.optionGroups
        });
        if (Array.isArray(conditionValue)) {
          return conditionValue
            .map((valueItem) => {
              const matchingOption = mergedOptions.find(
                (option) => option.key === valueItem
              );
              if (matchingOption) return matchingOption.label;
              return valueItem;
            })
            .join(', ');
        } else {
          const matchingOption = mergedOptions.find(
            (option) => option.key === conditionValue
          );
          if (matchingOption) return matchingOption.label;
        }
      }
    }

    if (Array.isArray(conditionValue)) {
      return conditionValue.join(', ') || '';
    }

    if (conditionValue === true) {
      return messages.commonYes;
    }

    if (conditionValue === false) {
      return messages.commonNo;
    }

    if (conditionValue === 'no including not set') {
      return messages.userAttributesNoIncludingNotSet;
    }

    return conditionValue || '';
  };

  return getValue();
};

export default GroupUserAttributeDisplayValue;
